/**
 * LegacyRedirectBanner — informational top-of-viewport notice that
 * fires once per session after a redirect from a retired SPA path.
 *
 * Why
 * ----
 * Slice 91 (audit GAP-MIGRATION-SAFETY-LEGACY) wires <LegacyRedirect>
 * routes for 13 killed paths. Without a banner, the user who clicks
 * an old bookmark lands on the canonical surface with no explanation
 * — the same UX hole the audit's §17 migration plan called out.
 *
 * Contract
 * --------
 *   - Reads the `LEGACY_REDIRECT_FLAG_KEY` written by <LegacyRedirect>.
 *   - Renders nothing if the flag is absent.
 *   - Renders a `role="status"` + `aria-live="polite"` notice with the
 *     SSOT-supplied copy + a Dismiss button.
 *   - Single-show per session: clears the flag on dismiss OR on the
 *     next route change after first paint (whichever comes first).
 *   - Does NOT block the redirect; the <Navigate replace> has already
 *     committed by the time this component reads sessionStorage.
 *
 * SRP
 * ---
 * This component owns ONLY presentation + session-flag lifecycle. It
 * never matches paths, never talks to the SSOT directly beyond the
 * flag key, and never fires telemetry on its own (the parent route
 * host can emit `legacy_redirect.followed` once the banner mounts).
 *
 * a11y
 * ----
 *   - `role="status"` + `aria-live="polite"`: screen readers announce
 *     the notice without interrupting the user's current task.
 *   - Dismiss button is keyboard-focusable + labelled.
 *   - The banner sits ABOVE main content so it never overlaps focus
 *     targets inside the canonical surface.
 *
 * Why a new component instead of NextStepCallout / EmptyStateCard?
 * ----------------------------------------------------------------
 * NextStepCallout is the "what now?" affordance after a TERMINAL
 * action; it carries a primary CTA + up to 3 secondaries. The legacy-
 * redirect banner is informational only (one Dismiss button, no
 * primary CTA the user must consider) and lives at the layout chrome
 * level, not inside a surface body. EmptyStateCard is for
 * empty-list surfaces. Neither shape matches; reusing them would
 * stretch their SRP. The banner stays its own ~50-line component
 * with the same idiom as the existing OfflineBanner.
 */

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { captureError } from '../errors/captureError';
import { LEGACY_REDIRECT_FLAG_KEY } from './legacyRedirects';
import './LegacyRedirectBanner.css';

type LegacyRedirectFlag = {
    from: string;
    to: string;
    bannerText: string;
};

function readFlag(): LegacyRedirectFlag | null {
    try {
        const raw = sessionStorage.getItem(LEGACY_REDIRECT_FLAG_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<LegacyRedirectFlag>;
        if (
            typeof parsed.from === 'string'
            && typeof parsed.to === 'string'
            && typeof parsed.bannerText === 'string'
        ) {
            return { from: parsed.from, to: parsed.to, bannerText: parsed.bannerText };
        }
        return null;
    } catch (err) {
        captureError(err, { scope: 'LegacyRedirectBanner', op: 'read-flag' });
        return null;
    }
}

function clearFlag(): void {
    try {
        sessionStorage.removeItem(LEGACY_REDIRECT_FLAG_KEY);
    } catch (err) {
        captureError(err, { scope: 'LegacyRedirectBanner', op: 'clear-flag' });
    }
}

function LegacyRedirectBanner() {
    // Seed from sessionStorage on first render. We snapshot the flag
    // into local state so subsequent route changes can clear the
    // storage key without immediately unmounting the banner — the
    // user gets to read the message at least once before navigating
    // away. The flag is cleared by `useEffect` below on the FIRST
    // location change after mount (single-show contract).
    const [flag, setFlag] = useState<LegacyRedirectFlag | null>(() => readFlag());
    const location = useLocation();

    // ── Single-show lifecycle ─────────────────────────────────────────
    //
    // On mount: clear the sessionStorage flag immediately so a hard
    // reload of the canonical surface doesn't re-show the banner.
    // (The redirect itself replaced the URL, so a reload lands on the
    // canonical path, not the legacy one — but a paranoid user who
    // hits back/forward could revisit. The session flag is one-shot
    // by design.)
    //
    // On location change AFTER mount: hide the banner in local state.
    // We use `location.pathname` as the dependency so internal updates
    // (e.g. a query-string toggle) don't dismiss the banner prematurely.
    useEffect(() => {
        if (flag) clearFlag();
        // Why: depending on `flag` would re-fire the effect when
        // `setFlag(null)` runs on Dismiss, causing a mid-render
        // `clearFlag` that fights React's commit phase. The
        // single-show contract documented at the top of this file
        // requires the clear to happen exactly once per mount or
        // pathname change. See legacyRedirects.ts for the SSOT.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot mount + pathname-only re-fire is the explicit single-show contract; see component header.
    }, [location.pathname]);

    // Hide on dismiss OR on a subsequent route change after the banner
    // has been visible. The local state mirrors the lifecycle so the
    // banner unmounts in the same render commit as the navigation.
    const onDismiss = () => setFlag(null);

    if (!flag) return null;

    return (
        <div
            className="legacy-redirect-banner"
            role="status"
            aria-live="polite"
        >
            <span className="legacy-redirect-banner__text">
                You followed an old bookmark. {flag.bannerText}
            </span>
            <button
                type="button"
                className="legacy-redirect-banner__dismiss"
                onClick={onDismiss}
                aria-label="Dismiss legacy redirect notice"
            >
                <X size={14} aria-hidden="true" />
                <span>Dismiss</span>
            </button>
        </div>
    );
}

export default LegacyRedirectBanner;
